import { Box, Button, Paper, Typography } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { SearchOff, ArrowBack } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { ROUTES } from '../../constants/routes';

interface Props {
  /** What kind of entity wasn't found — used in the message text. */
  entity?: 'test' | 'work order' | 'sample';
  /** Optional override for the explanatory message. */
  message?: string;
}

/**
 * Empty-state shown by test-workflow pages when the WorkOrderDetail / test
 * row referenced in the URL is missing or soft-deleted on the server.
 *
 * This replaces the previous behaviour of toasting "Failed to load test data"
 * (which made a missing test look like a transient infrastructure problem
 * and led users — including via moftaki-fix verification links — to suspect
 * a backend outage). A clear "test not found" + "Back to Work Orders" button
 * gives the user the right mental model and a way out.
 */
export const TestNotFound = ({ entity = 'test', message }: Props) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { projectId, workOrderId } = useParams<{ projectId?: string; workOrderId?: string }>();

  const goBack = () => {
    if (projectId && workOrderId) {
      navigate(ROUTES.WORK_ORDER_DETAIL.replace(':projectId', projectId).replace(':workOrderId', workOrderId));
    } else if (projectId) {
      navigate(ROUTES.WORK_ORDERS.replace(':projectId', projectId));
    } else {
      navigate(ROUTES.PROJECTS);
    }
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
      <Paper
        elevation={0}
        sx={{
          p: 4, maxWidth: 480, textAlign: 'center', borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.warning.main, 0.4)}`,
          backgroundColor: alpha(theme.palette.warning.light, 0.06),
        }}
      >
        <SearchOff sx={{ fontSize: 56, color: 'warning.main', mb: 1 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: 'text.primary' }}>
          {`This ${entity} no longer exists`}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3, lineHeight: 1.55 }}>
          {message ?? `The ${entity} referenced by this URL was removed or was never created. This isn’t a server error — the row simply isn’t there to load.`}
        </Typography>
        <Button
          variant="contained"
          startIcon={<ArrowBack />}
          onClick={goBack}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {projectId && workOrderId ? 'Back to Work Order' : projectId ? 'Back to Work Orders' : 'Back to Projects'}
        </Button>
      </Paper>
    </Box>
  );
};
